import * as THREE from 'three';
import { safeZoneUniforms, SAFE_ZONE_VERTEX_CHUNK } from '../shaders/safeZone.glsl.js';

// ConfettiLayer - metallised paper fired from cannons, fluttering down
// through a stage's beams and sparkling only where the light touches it.
// Extracted from the standalone Confetti visualizer (2026-10-12) when the
// brief turned it into a LAYER of MainStage and Bayfront instead of a
// composition of its own: the physics, the specular and the shot slots are
// that visualizer's; what is new is that the layer lives in a host's world
// space, at the host's SCALE, lit by the host's lights, and fired on the
// host's cadence.
//
// THE FLIGHT IS ANALYTIC, PER PIECE, IN THE VERTEX SHADER. A piece is a
// flat quad with a long axis, tumbling about that axis at its own 2-6
// turns a second, the axis itself wobbling slowly. Its fall speed DEPENDS
// on where the tumble has it: flat it planes (0.25 unit/s), on edge it
// dives (0.9), and the vertical displacement is the closed-form integral
// of that speed over the tumble, so a piece hesitates, dives and recovers
// without a CPU update. A lateral flutter rides on top in the planing
// direction. The launch is a 25-degree cone burst with exponential drag;
// the fall takes over as the burst dies. A piece that reaches the ground
// lies flat there until its SLOT is fired again: a shot writes one slot's
// uniforms (time, origin, direction, strength, live fraction), a piece
// knows only its slot and its seeds. Nothing is uploaded per frame.
//
// THE SPARKLE IS A SPECULAR. |n . H|^S against the half vector of each
// light and the camera; the tumble sweeps the lobe past the lens in a
// flash. The axis must wobble (a fixed axis sweeps one great circle, which
// misses the half vector for most pieces and never flashes) and the lobe
// must be wide enough to last a frame (S 12-40). Outside the lights a piece
// gets 0.4 % of its tint - 2 % already read as 28/255 after sRGB.
//
// UNITS. Everything is stated in "confetti units" (the standalone's world:
// the cannons six units from the lens) and multiplied by `unit`, the host's
// world units per confetti unit, so MainStage (unit 2) and Bayfront (unit
// 7) get the same apparent paper, the same flight and the same sparkle.

export const SLOTS = 8;
const SHOT_MS = 150;
const CONE_DEG = 25;
const LAUNCH_SPEED = 6.0;
const DRAG_K = 1.8;
const FALL_EDGE = 0.9;
const FALL_FLAT = 0.25;
const FLUTTER_AMP = 0.15;
const FLUTTER_T = [0.6, 1.4];
const TUMBLE_TPS = [2.0, 6.0];
const SIZE = [0.03, 0.06];
const SHAPES = [[2.0, 1.0, 0.70], [1.0, 1.0, 0.20], [4.0, 0.5, 0.10]];
const SILVER_SHARE = 0.30;
const GOLD_SHARE = 0.15;
const METAL_SAT = 0.90;
const DARK = 0.004;
const IN_BEAM = 0.30;
const FLASH_GAIN = 2.5;
const SPEC_RANGE = [12.0, 40.0];
export const LIGHTS = 6;
const BEAM_TAN = 0.08;
const BEAM_R0 = 0.2;
const BEAM_FALL_K = 0.006;
const NEAR_CULL = 1.2;
export const RIBBONS = 12;
const RIBBON_SEGS = 40;
const RIBBON_W = 0.06;
const RIBBON_TRAIL = 1.1;
const RIBBON_SPEED = 6.5;
const RIBBON_FALL = 0.16;
const RIBBON_TWIST = 9.0;
const SAFE_FLOOR = 0.50;

function lerp(a, b, t) { return a + (b - a) * t; }
function srgbToLinear(c) { return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); }

// The lights and the metallic lobe, shared by the pieces and the ribbons.
const LIGHT_CHUNK = `
  uniform vec3 uBeamO[${LIGHTS}];
  uniform vec3 uBeamD[${LIGHTS}];
  uniform vec3 uBeamC[${LIGHTS}];     // colour times level; black = off
  uniform float uFlash;
  uniform float uSpec;
  uniform vec3 uCamPos;
  uniform float uUnit;
  uniform float uAmbient;       // the host's wash on the paper (an LED wall behind it): 0 for the standalone's dark
  vec3 shade(vec3 pos, vec3 n, vec3 tint, float metal) {
    vec3 V = normalize(uCamPos - pos);
    vec3 col = tint * (${DARK.toFixed(3)} + uAmbient);
    vec3 flashTint = mix(mix(tint, vec3(1.0), 0.55), vec3(1.0, 0.93, 0.78), metal);
    for (int i = 0; i < ${LIGHTS}; i++) {
      vec3 bc = uBeamC[i];
      if (dot(bc, bc) < 1e-6) continue;
      vec3 d = pos - uBeamO[i];
      float t = dot(d, uBeamD[i]) / uUnit;
      if (t <= 0.2) continue;
      vec3 perp = d - uBeamD[i] * (t * uUnit);
      float r2 = dot(perp, perp) / (uUnit * uUnit);
      float R = ${BEAM_R0.toFixed(2)} + t * ${BEAM_TAN.toFixed(3)};
      float inB = 1.0 - smoothstep(0.55 * R * R, R * R, r2);
      if (inB <= 0.0) continue;
      inB /= 1.0 + t * t * ${BEAM_FALL_K.toFixed(3)};
      vec3 L = normalize(uBeamO[i] - pos);
      vec3 H = normalize(L + V);
      float lobe = pow(abs(dot(n, H)), uSpec);
      col += mix(bc, vec3(1.0) * max(max(bc.r, bc.g), bc.b), uFlash) * inB * (tint * ${IN_BEAM.toFixed(2)} + flashTint * (lobe * ${FLASH_GAIN.toFixed(2)}));
    }
    return col;
  }
  float fogOf(vec3 pos, float density) {
    float d = distance(pos, uCamPos) * density;
    return exp(-d * d);
  }
`;

const pieceVertexShader = `
  precision mediump float;
  attribute vec4 aSeed;
  attribute float aSlot;
  attribute vec3 aShape;        // w, h (confetti units), colour index
  attribute vec4 aMotion;       // yaw, tumble rad/s, theta0, flutter period
  uniform float uTime;
  uniform float uShotT[${SLOTS}];
  uniform vec3 uShotO[${SLOTS}];
  uniform vec3 uShotD[${SLOTS}];
  uniform float uShotS[${SLOTS}];
  uniform float uShotN[${SLOTS}];
  uniform vec3 uColors[6];
  uniform float uGround;
  uniform float uFog;
  ${LIGHT_CHUNK}
  ${SAFE_ZONE_VERTEX_CHUNK}
  varying vec3 vColor;
  varying float vSafe;
  void main() {
    int slot = int(aSlot + 0.5);
    int ci = int(aShape.z + 0.5);
    vec3 tint = uColors[ci];
    float metal = ci >= 4 ? 1.0 : 0.0;
    float yaw = aMotion.x, omega = aMotion.y, theta0 = aMotion.z, T = aMotion.w;
    float yw = yaw + 0.7 * sin(uTime * 0.9 + theta0);
    float tilt = 0.55 * sin(uTime * 0.7 + yaw * 3.0);
    vec3 axis = normalize(vec3(cos(yw), tilt, sin(yw)));
    vec3 h = normalize(cross(vec3(0.0, 1.0, 0.0), axis));
    vec3 upA = cross(axis, h);
    float t0 = uShotT[slot];
    float a = uTime - t0 - aSeed.w * ${(SHOT_MS / 1000).toFixed(3)};
    if (a < 0.0 || aSeed.w > uShotN[slot]) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); vColor = vec3(0.0); vSafe = 0.0; return; }
    vec3 D = uShotD[slot];
    vec3 u1 = normalize(cross(D, vec3(1.0, 0.0, 0.0)));
    vec3 u2 = cross(D, u1);
    float r = ${(CONE_DEG * Math.PI / 180).toFixed(4)} * sqrt(aSeed.x);
    float az = 6.2831853 * aSeed.y;
    vec3 dir = normalize(D * cos(r) + (u1 * cos(az) + u2 * sin(az)) * sin(r));
    float v0 = ${LAUNCH_SPEED.toFixed(1)} * uShotS[slot] * (0.7 + 0.6 * aSeed.z);
    float burst = (1.0 - exp(-${DRAG_K.toFixed(2)} * a)) / ${DRAG_K.toFixed(2)};
    vec3 pos = uShotO[slot] + dir * (v0 * burst * uUnit);
    float af = a - burst;
    float theta = theta0 + omega * af;
    float fall = ${FALL_EDGE.toFixed(2)} * af - ${(FALL_EDGE - FALL_FLAT).toFixed(2)} * (0.5 * af + (sin(2.0 * theta) - sin(2.0 * theta0)) / (4.0 * omega));
    pos.y -= fall * uUnit;
    pos += h * (${FLUTTER_AMP.toFixed(2)} * uUnit * sin(6.2831853 * af / T + theta0) * smoothstep(0.0, 1.0, af));
    float landed = 0.0;
    if (pos.y <= uGround) { pos.y = uGround; landed = 1.0; }
    vec3 toCam = pos - uCamPos;
    if (dot(toCam, toCam) < ${(NEAR_CULL * NEAR_CULL).toFixed(2)} * uUnit * uUnit) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); vColor = vec3(0.0); vSafe = 0.0; return; }
    vec3 n = landed > 0.5 ? vec3(0.0, 1.0, 0.0) : (upA * cos(theta) + h * sin(theta));
    vec3 v = cross(n, axis);
    vec3 world = pos + axis * (position.x * aShape.x * uUnit) + v * (position.y * aShape.y * uUnit);
    vColor = shade(pos, n, tint, metal) * fogOf(pos, uFog);
    vec4 clip = projectionMatrix * viewMatrix * vec4(world, 1.0);
    vec4 centre = projectionMatrix * viewMatrix * vec4(pos, 1.0);
    float att = safeZoneAttenuation(centre);
    vSafe = ${SAFE_FLOOR.toFixed(2)} + (att - 0.32) * ${((1 - SAFE_FLOOR) / 0.68).toFixed(5)};
    gl_Position = clip;
  }
`;
const pieceFragmentShader = `
  precision mediump float;
  varying vec3 vColor;
  varying float vSafe;
  void main() { gl_FragColor = vec4(vColor * vSafe, 1.0); }
`;

const ribbonVertexShader = `
  precision mediump float;
  attribute float aS;
  attribute float aSide;
  attribute float aRibbon;
  uniform float uTime;
  uniform float uRibT[${RIBBONS}];
  uniform vec3 uRibO[${RIBBONS}];
  uniform vec3 uRibD[${RIBBONS}];
  uniform vec3 uRibC[${RIBBONS}];
  uniform float uGround;
  uniform float uFog;
  ${LIGHT_CHUNK}
  ${SAFE_ZONE_VERTEX_CHUNK}
  varying vec3 vColor;
  varying float vSafe;
  vec3 pathAt(vec3 O, vec3 D, float a, float seed) {
    float burst = (1.0 - exp(-1.6 * a)) / 1.6;
    vec3 p = O + D * (${RIBBON_SPEED.toFixed(1)} * burst * uUnit);
    float af = max(a - burst, 0.0);
    p.y -= ${RIBBON_FALL.toFixed(2)} * af * uUnit;
    p.x += 0.25 * uUnit * sin(af * 1.7 + seed * 6.283);
    p.z += 0.18 * uUnit * sin(af * 1.3 + seed * 4.1);
    p.y = max(p.y, uGround);
    return p;
  }
  void main() {
    int i = int(aRibbon + 0.5);
    float ah = uTime - uRibT[i];
    if (ah < 0.0) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); vColor = vec3(0.0); vSafe = 0.0; return; }
    float seed = fract(aRibbon * 0.618034);
    float a = max(ah - aS * ${RIBBON_TRAIL.toFixed(2)}, 0.0);
    vec3 p = pathAt(uRibO[i], uRibD[i], a, seed);
    vec3 pn = pathAt(uRibO[i], uRibD[i], a + 0.03, seed);
    vec3 tang = normalize(pn - p + vec3(1e-4, 0.0, 0.0));
    vec3 side0 = normalize(cross(tang, vec3(0.0, 1.0, 0.0)) + vec3(0.0, 0.0, 1e-3));
    vec3 side1 = cross(tang, side0);
    float psi = aS * ${(RIBBON_TWIST * 2 * Math.PI).toFixed(3)} + ah * 2.5 + seed * 6.283;
    vec3 w = side0 * cos(psi) + side1 * sin(psi);
    vec3 n = cross(tang, w);
    vec3 world = p + w * (aSide * ${RIBBON_W.toFixed(3)} * uUnit);
    vColor = shade(p, n, uRibC[i], 1.0) * (1.0 - smoothstep(0.75, 1.0, aS)) * fogOf(p, uFog);
    vec4 clip = projectionMatrix * viewMatrix * vec4(world, 1.0);
    vec4 centre = projectionMatrix * viewMatrix * vec4(p, 1.0);
    float att = safeZoneAttenuation(centre);
    vSafe = ${SAFE_FLOOR.toFixed(2)} + (att - 0.32) * ${((1 - SAFE_FLOOR) / 0.68).toFixed(5)};
    gl_Position = clip;
  }
`;

// Materials cached at module level, keyed by what shapes the shader: the
// Pi compiles these in a second and three.js frees the program with the
// last material. Uniform holders live with them; a layer adopts them and
// resets what its update does not write every frame.
const MATERIALS = new Map();
function materialsFor(depthTest) {
  const key = depthTest ? 'd' : 'n';
  let m = MATERIALS.get(key);
  if (m) return m;
  const shared = {
    uTime: { value: 0 },
    uBeamO: { value: Array.from({ length: LIGHTS }, () => new THREE.Vector3(0, 100, 0)) },
    uBeamD: { value: Array.from({ length: LIGHTS }, () => new THREE.Vector3(0, -1, 0)) },
    uBeamC: { value: Array.from({ length: LIGHTS }, () => new THREE.Vector3(0, 0, 0)) },
    uFlash: { value: 0 },
    uSpec: { value: 26 },
    uCamPos: { value: new THREE.Vector3() },
    uUnit: { value: 1 },
    uAmbient: { value: 0 },
    uGround: { value: 0 },
    uFog: { value: 0 },
    uAspect: safeZoneUniforms.uAspect,
    uSafeCenter: safeZoneUniforms.uSafeCenter,
    uSafeHalfSize: safeZoneUniforms.uSafeHalfSize,
    uSafeFeather: safeZoneUniforms.uSafeFeather
  };
  const pieceUniforms = Object.assign({}, shared, {
    uShotT: { value: new Float32Array(SLOTS).fill(1e9) },
    uShotO: { value: Array.from({ length: SLOTS }, () => new THREE.Vector3()) },
    uShotD: { value: Array.from({ length: SLOTS }, () => new THREE.Vector3(0, 1, 0)) },
    uShotS: { value: new Float32Array(SLOTS).fill(1) },
    uShotN: { value: new Float32Array(SLOTS).fill(1) },
    uColors: { value: Array.from({ length: 6 }, () => new THREE.Vector3(0.8, 0.8, 0.8)) }
  });
  const pieces = new THREE.ShaderMaterial({
    uniforms: pieceUniforms, vertexShader: pieceVertexShader, fragmentShader: pieceFragmentShader,
    transparent: true, depthTest: depthTest, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide
  });
  const ribbonUniforms = Object.assign({}, shared, {
    uRibT: { value: new Float32Array(RIBBONS).fill(1e9) },
    uRibO: { value: Array.from({ length: RIBBONS }, () => new THREE.Vector3()) },
    uRibD: { value: Array.from({ length: RIBBONS }, () => new THREE.Vector3(0, 1, 0)) },
    uRibC: { value: Array.from({ length: RIBBONS }, () => new THREE.Vector3(1, 0.9, 0.7)) }
  });
  const ribbons = new THREE.ShaderMaterial({
    uniforms: ribbonUniforms, vertexShader: ribbonVertexShader, fragmentShader: pieceFragmentShader,
    transparent: true, depthTest: depthTest, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide
  });
  m = { shared, pieceUniforms, pieces, ribbonUniforms, ribbons };
  MATERIALS.set(key, m);
  return m;
}

export class ConfettiLayer {
  // options: { max, ribbons, unit, groundY, fog, ambient, depthTest, renderOrder }
  constructor(options) {
    const o = options || {};
    this._max = Math.max(SLOTS, o.max || 6000);
    this._withRibbons = !!o.ribbons;
    this._unit = o.unit || 1;
    this._groundY = o.groundY || 0;
    this._fog = o.fog || 0;
    this._ambient = o.ambient || 0;
    this._depthTest = o.depthTest !== false;
    this._renderOrder = o.renderOrder != null ? o.renderOrder : 5;
    this._time = 0;
    this._slotTime = new Float32Array(SLOTS).fill(-1e9);
    this._ribbonTime = new Float32Array(RIBBONS).fill(-1e9);
    this._shots = 0;
    this._palette = [0xff3366, 0x33aaff, 0xffcc33, 0x66ff99];
  }

  init(scene) {
    this._scene = scene;
    this._mats = materialsFor(this._depthTest);
    const s = this._mats.shared, p = this._mats.pieceUniforms, r = this._mats.ribbonUniforms;
    s.uTime.value = 0; s.uFlash.value = 0; s.uSpec.value = 26; s.uUnit.value = this._unit; s.uGround.value = this._groundY; s.uFog.value = this._fog; s.uAmbient.value = this._ambient;
    for (let i = 0; i < LIGHTS; i++) s.uBeamC.value[i].set(0, 0, 0);
    p.uShotT.value.fill(1e9); p.uShotN.value.fill(1); p.uShotS.value.fill(1);
    r.uRibT.value.fill(1e9);
    this.setPalette(this._palette);
    this._buildPieces();
    if (this._withRibbons) this._buildRibbons();
  }

  _buildPieces() {
    const N = this._max;
    const perSlot = Math.floor(N / SLOTS);
    this._perSlot = perSlot;
    const geo = new THREE.InstancedBufferGeometry();
    const quad = new Float32Array([-0.5, -0.5, 0, 0.5, -0.5, 0, 0.5, 0.5, 0, -0.5, 0.5, 0]);
    geo.setAttribute('position', new THREE.BufferAttribute(quad, 3));
    geo.setIndex([0, 1, 2, 0, 2, 3]);
    const seed = new Float32Array(N * 4), slot = new Float32Array(N), shape = new Float32Array(N * 3), motion = new Float32Array(N * 4);
    const rnd = Math.random;
    for (let i = 0; i < N; i++) {
      seed[i * 4] = rnd(); seed[i * 4 + 1] = rnd(); seed[i * 4 + 2] = rnd(); seed[i * 4 + 3] = rnd();
      slot[i] = Math.min(SLOTS - 1, Math.floor(i / perSlot));
      const r = rnd();
      const sh = r < SHAPES[0][2] ? SHAPES[0] : (r < SHAPES[0][2] + SHAPES[1][2] ? SHAPES[1] : SHAPES[2]);
      const size = lerp(SIZE[0], SIZE[1], rnd());
      shape[i * 3] = size * sh[0] * sh[1]; shape[i * 3 + 1] = size * sh[1];
      const c = rnd();
      shape[i * 3 + 2] = c < SILVER_SHARE ? 4 : (c < SILVER_SHARE + GOLD_SHARE ? 5 : Math.floor(rnd() * 4));
      motion[i * 4] = rnd() * Math.PI * 2;
      motion[i * 4 + 1] = lerp(TUMBLE_TPS[0], TUMBLE_TPS[1], rnd()) * Math.PI * 2;
      motion[i * 4 + 2] = rnd() * Math.PI * 2;
      motion[i * 4 + 3] = lerp(FLUTTER_T[0], FLUTTER_T[1], rnd());
    }
    const mk = (n, arr, size) => { const a = new THREE.InstancedBufferAttribute(arr, size); a.setUsage(THREE.StaticDrawUsage); geo.setAttribute(n, a); };
    mk('aSeed', seed, 4); mk('aSlot', slot, 1); mk('aShape', shape, 3); mk('aMotion', motion, 4);
    geo.instanceCount = N;
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 0, 0), 1e4);
    this._pieceGeo = geo;
    this._pieces = new THREE.Mesh(geo, this._mats.pieces);
    this._pieces.frustumCulled = false;
    this._pieces.renderOrder = this._renderOrder;
    this._scene.add(this._pieces);
  }

  _buildRibbons() {
    const n = RIBBONS, segs = RIBBON_SEGS;
    const verts = n * (segs + 1) * 2;
    const pos = new Float32Array(verts * 3), s = new Float32Array(verts), side = new Float32Array(verts), rib = new Float32Array(verts);
    const idx = [];
    for (let r = 0; r < n; r++) {
      for (let k = 0; k <= segs; k++) {
        for (let d = 0; d < 2; d++) { const v = (r * (segs + 1) + k) * 2 + d; s[v] = k / segs; side[v] = d === 0 ? -1 : 1; rib[v] = r; }
        if (k < segs) { const b = (r * (segs + 1) + k) * 2; idx.push(b, b + 1, b + 2, b + 1, b + 3, b + 2); }
      }
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    geo.setAttribute('aS', new THREE.BufferAttribute(s, 1));
    geo.setAttribute('aSide', new THREE.BufferAttribute(side, 1));
    geo.setAttribute('aRibbon', new THREE.BufferAttribute(rib, 1));
    geo.setIndex(idx);
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 0, 0), 1e4);
    this._ribbonGeo = geo;
    this._ribbons = new THREE.Mesh(geo, this._mats.ribbons);
    this._ribbons.frustumCulled = false;
    this._ribbons.renderOrder = this._renderOrder + 1;
    this._scene.add(this._ribbons);
  }

  // The four tints from the host's palette (metallised: saturation 0.9),
  // silver and gold fixed.
  setPalette(hexes) {
    if (hexes && hexes.length) this._palette = hexes.slice(0, 4);
    const u = this._mats.pieceUniforms.uColors.value;
    this._paletteLinear = [];
    for (let i = 0; i < 4; i++) {
      const c = new THREE.Color(this._palette[i % this._palette.length]); c.getHSL(_hsl);
      c.setHSL(_hsl.h, METAL_SAT, 0.55);
      const lin = [srgbToLinear(c.r), srgbToLinear(c.g), srgbToLinear(c.b)];
      this._paletteLinear.push(lin);
      u[i].set(lin[0], lin[1], lin[2]);
    }
    u[4].set(0.85, 0.87, 0.9);
    u[5].set(0.95, 0.72, 0.28);
  }

  // A light the paper sparkles in: origin, direction, LINEAR colour, level
  // (0 switches it off). Up to LIGHTS of them.
  setLight(i, origin, direction, color, level) {
    if (i < 0 || i >= LIGHTS) return;
    const s = this._mats.shared;
    s.uBeamO.value[i].copy(origin);
    s.uBeamD.value[i].copy(direction).normalize();
    s.uBeamC.value[i].set(color.r != null ? color.r : color.x, color.g != null ? color.g : color.y, color.b != null ? color.b : color.z).multiplyScalar(level != null ? level : 1);
  }
  setAmbient(a) { this._ambient = a; this._mats.shared.uAmbient.value = a; }
  setFlash(f) { this._mats.shared.uFlash.value = Math.max(0, Math.min(1, f)); }
  setCamera(camera) { this._mats.shared.uCamPos.value.copy(camera.position); }

  _oldestSlot() {
    let best = 0;
    for (let i = 1; i < SLOTS; i++) if (this._slotTime[i] < this._slotTime[best]) best = i;
    return best;
  }
  // A cannon shot: origin and direction in world units, strength (1 = the
  // standalone's), live fraction of the slot's pieces (density).
  fire(origin, direction, strength, frac) {
    const slot = this._oldestSlot();
    const p = this._mats.pieceUniforms;
    this._slotTime[slot] = this._time;
    p.uShotT.value[slot] = this._time;
    p.uShotO.value[slot].copy(origin);
    p.uShotD.value[slot].copy(direction).normalize();
    p.uShotS.value[slot] = strength != null ? strength : 1;
    p.uShotN.value[slot] = frac != null ? frac : 1;
    this._shots++;
    return slot;
  }
  // Six serpentines from the given cannons ({ origin, direction }), in the palette.
  fireRibbons(cannons) {
    if (!this._ribbons || !cannons.length) return;
    const r = this._mats.ribbonUniforms;
    let base = 0;
    for (let i = 1; i < RIBBONS; i++) if (this._ribbonTime[i] < this._ribbonTime[base]) base = i;
    const start = base < RIBBONS / 2 ? 0 : RIBBONS / 2;
    for (let k = 0; k < RIBBONS / 2; k++) {
      const i = start + k, c = cannons[k % cannons.length];
      this._ribbonTime[i] = this._time;
      r.uRibT.value[i] = this._time;
      r.uRibO.value[i].copy(c.origin);
      _tmp.copy(c.direction).normalize();
      _tmp.x += (Math.random() - 0.5) * 0.4; _tmp.z += (Math.random() - 0.5) * 0.3;
      r.uRibD.value[i].copy(_tmp).normalize();
      const col = this._paletteLinear[k % 4];
      r.uRibC.value[i].set(col[0], col[1], col[2]).lerp(_warm, 0.5);
    }
  }

  // Not called while the host is paused: the paper freezes in the air.
  update(dt, high) {
    this._time += dt;
    const s = this._mats.shared;
    s.uTime.value = this._time;
    s.uSpec.value = lerp(SPEC_RANGE[0], SPEC_RANGE[1], Math.max(0, Math.min(1, high != null ? high : 0.5)));
  }

  get time() { return this._time; }
  get shots() { return this._shots; }
  get perSlot() { return this._perSlot; }

  dispose() {
    if (this._pieces) { this._scene.remove(this._pieces); this._pieceGeo.dispose(); }
    if (this._ribbons) { this._scene.remove(this._ribbons); this._ribbonGeo.dispose(); }
    this._pieces = this._ribbons = null;
    this._scene = null;
  }
}

const _hsl = { h: 0, s: 0, l: 0 };
const _tmp = new THREE.Vector3();
const _warm = new THREE.Vector3(1, 0.9, 0.7);
